import React, { PureComponent } from 'react';
import { MapConvection } from 'app/components/map/CoreMap';
import { DEFAULT_FL_FILTER_RANGE } from 'app/components/dashboard/network/AbstractMapView';
import AppContext from 'app/common/context/AppContext';
import { Flex } from 'app/components/common/Flex';
import {
    EnvironmentLayerUtils,
    MapLayer,
    MapLayerType,
    MapLayerUtils,
    MapObjectType,
    ObjectToDisplay,
    RegulatedLayerDelayLevelEnum,
    WeatherLayerUtils,
    WeatherMinLevel,
} from 'app/components/dashboard/network/map/Map';
import { MonitoredMeasure } from 'app/model/SOL49';
import { HotspotDto, RegulationDto, RegulationOrMcdmOnlyDto, RegulationState, TimePeriod } from 'app/model/CORE';
import MapUtils, { HotspotMap, MonitorableMapMeasure } from 'app/components/map/util/MapUtils';
import {
    MapTrafficVolumeCacheType,
    MapTrafficVolumeCacheUpdated,
} from 'app/components/dashboard/network/map/MapTrafficVolumeCache';
import MeasureUtils from 'app/common/util/MeasureUtils';
import difference from 'lodash/difference';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { notification } from 'antd';
import Utils from 'app/common/util/Utils';
import LayerSelectionEntry from 'app/components/dashboard/network/map/LayerSelectionEntry';
import { RootState } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';
import { UnityApplication } from 'app/unity/UnityApplication';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';

const mapStateToProps = (state: RootState) => {
    return {
        tvCache: state.mapFullscreenData.tvCache,
    };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type NetworkMapPanelProps = {
    regulations: RegulationDto[];
    hotspots: HotspotDto[];
    onMapUpdate: (map: string) => void;
};

class NetworkMapPanel extends PureComponent<NetworkMapPanelProps & PropsFromRedux> {
    public static contextType = NMUI4DContext;
    private nmui4dContext?: NMUI4DContextProps;

    componentDidMount(): void {
        this.nmui4dContext = this.context as NMUI4DContextProps;

        AppContext.eventBus.register(MapTrafficVolumeCacheUpdated.NAME, (event: MapTrafficVolumeCacheUpdated) => {
            if (this.hasRelevantCacheUpdates(event.previousCache, event.cache)) {
                //only refresh map if the new cache value has relevant data for the layer
                this.refreshMeasures(event.cache);
            }
        });
        this.initWeatherLayer();
    }

    componentDidUpdate(prevProps: Readonly<NetworkMapPanelProps>, prevState: Readonly<{}>, snapshot?: any) {
        if (
            Utils.isNotEquals(prevProps.regulations, this.props.regulations) ||
            Utils.isNotEquals(prevProps.hotspots, this.props.hotspots)
        ) {
            this.refreshMeasures(this.props.tvCache);
        }
    }

    private hasRelevantCacheUpdates(prevCache: MapTrafficVolumeCacheType, cache: MapTrafficVolumeCacheType) {
        const layerTvIds = this.props.regulations.map(r => MeasureUtils.getTvLocation(r).id);
        const prevCacheTvIds = Object.keys(prevCache).filter(tvId => layerTvIds.includes(tvId));
        const cacheTvIds = Object.keys(cache).filter(tvId => layerTvIds.includes(tvId));
        return difference(cacheTvIds, prevCacheTvIds).length > 0;
    }

    private initWeatherLayer = () => {
        const weatherLayer = {
            objectsToDisplay: undefined,
            selection: [],
            label: 'Weather',
            mapType: [MapObjectType.CONVECTION],
            priority: 1,
            showLabel: false,
            active: true,
            type: MapLayerType.WEATHER,
            id: 4,
            group: WeatherLayerUtils.GROUP_LAYER,
            customProperties: {
                [WeatherLayerUtils.CUSTOM_PROPERTY_LEGEND]: false,
                [WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL]: new WeatherMinLevel(1),
            },
        };

        ServiceFactory.sol49Service()
            .getMapConvection({
                convectionSource: AppContext.getConvectionSource(),
                predictionDay: AppContext.getMonitoredDay(),
            })
            .then(reply => {
                const convectionData = reply?.data;
                // Display all convections if configured on Metsafe, otherwise display only current time to avoid overlap of DWD polygons
                // TODO may display nothing if outside of DWD weather periods (09h-21h)
                const period: TimePeriod = { from: AppContext.getCurrentTime(), to: AppContext.getCurrentTime() };
                const objectsToDisplay = MapLayerUtils.toObjectToDisplayArray(
                    MapUtils.getConvectionMap(
                        convectionData?.networkForecast,
                        convectionData?.airspaceDefinition,
                        period,
                    ),
                    MapObjectType.CONVECTION,
                );
                const weatherMapObjects = objectsToDisplay.map(obj => obj.object as MapConvection);
                // this.coreMapRef.current?.refreshLayer(weatherLayer, weatherMapObjects, period, {
                //     min: FlightLevelUtils.ON_GROUND_LEVEL,
                //     max: FlightLevelUtils.UNLIMITED_LEVEL,
                // });
            })
            .catch(error => {
                console.log('error: ', error);
                notification.error({
                    message: 'Error getting weather convection',
                    description: '' + error,
                    duration: 0,
                });
            });
    };

    private refreshMeasures = (cache: MapTrafficVolumeCacheType) => {
        const activeMeasures: MonitoredMeasure[] = this.props.regulations
            .filter(
                r => r.regulationState === RegulationState.APPLIED || r.regulationState === RegulationState.APPLYING,
            )
            .map(r => ({ measure: r, delay: -1 }));

        const regulationsLayer = {
            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(activeMeasures, MapObjectType.MEASURE),
            selection: [],
            label: 'Regulated',
            mapType: [MapObjectType.MEASURE],
            priority: 5.1,
            showLabel: true,
            active: true,
            type: MapLayerType.MEASURE,
            id: 2,
            customProperties: {
                [EnvironmentLayerUtils.REGULATED_SECTORS]: true,
                [EnvironmentLayerUtils.REGULATED_LAYER_MIN_DELAY]: RegulatedLayerDelayLevelEnum.ANY,
            },
        };

        const hotspotLayer: MapLayer = {
            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(this.props.hotspots, MapObjectType.HOTSPOT),
            selection: [],
            label: 'Hotspot',
            mapType: [MapObjectType.HOTSPOT],
            priority: 5.2,
            showLabel: true,
            active: true,
            type: MapLayerType.HOTSPOT,
            id: 3,
            customProperties: {},
        };

        const missingTvs = LayerSelectionEntry.retrievesRequiredDefinitions(cache, [
            ...regulationsLayer.objectsToDisplay,
            ...hotspotLayer.objectsToDisplay,
        ]);
        if (!missingTvs) {
            const regulationMapObjects = regulationsLayer.objectsToDisplay.map((obj, index) =>
                this.getRegulationMapObject(obj, index, cache),
            );
            // this.coreMapRef.current?.refreshLayer(regulationsLayer, regulationMapObjects, AppContext.getTodayPeriod(), {
            //     min: FlightLevelUtils.ON_GROUND_LEVEL,
            //     max: FlightLevelUtils.UNLIMITED_LEVEL,
            // });

            const hotspotMapObjects = hotspotLayer.objectsToDisplay.map((obj, index) =>
                this.getHotspotMapObject(obj, index, cache),
            );
            // this.coreMapRef.current?.refreshLayer(hotspotLayer, hotspotMapObjects, AppContext.getTodayPeriod(), {
            //     min: FlightLevelUtils.ON_GROUND_LEVEL,
            //     max: FlightLevelUtils.UNLIMITED_LEVEL,
            // });
        }
    };

    private getRegulationMapObject = (obj: ObjectToDisplay, index: number, cache: MapTrafficVolumeCacheType) => {
        return {
            measure: obj.object,
            index: index,
            mapTv: cache[(obj.object['measure'] as RegulationOrMcdmOnlyDto).location.id],
            selected: false,
        } as MonitorableMapMeasure;
    };

    private getHotspotMapObject = (obj: ObjectToDisplay, index: number, cache: MapTrafficVolumeCacheType) => {
        return {
            hotspot: obj.object,
            index: index,
            mapTv: cache[(obj.object as HotspotDto).hotspotId.tvId],
            selected: false,
        } as HotspotMap;
    };

    render() {
        // NOTE(bjarne): In mapless mode the UnityApplication is already created in App.tsx.
        if (this.nmui4dContext?.isMapless) {
            return null;
        }

        return (
            <Flex className="inp-map-panel" style={{ height: '70em' }}>
                <UnityApplication></UnityApplication>
            </Flex>
        );
    }
}

export default connector(NetworkMapPanel);
